import React, {useContext, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useFeatureFlags} from "utilities/featureUtil";
import {MessagesContext} from "../../panel/conversations/ConversationsPanel";
// Redux
import {appendMessage, editMessages} from "redux/actions/messagesActions";
import {createAction} from "redux/actions/experiencesActions";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
// Styles & components
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
import FlexContainer from "connectui/component/layout/FlexContainer";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import AttachmentsInput from "../input/AttachmentsInput";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import MobileChannelRow from "./MobileChannelRow";
import Divider from "connectui/component/display/Divider";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import MobileAutoMessageModal from "../modal/MobileAutoMessageModal";

const useStyles = (theme) =>
  buildStyles({
    row: {width: "100%", padding: theme.spacing(0, 4)},
    icon: {
      width: "20px",
      height: "20px",
      color: theme.colors.gray600,
    },
    noteButton: {
      width: "20px",
      height: "20px",
      padding: theme.spacing(0.5),
      color: theme.colors.white,
    },
  });

function CircleButton({icon, iconProps, onClick}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  return (
    <ButtonIcon
      icon={
        <SvgIcon
          component={ICONS[icon]}
          className={classes.icon}
          {...iconProps}
        />
      }
      sx={{
        backgroundColor: theme.colors.gray100,
        padding: theme.spacing(2),
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        "&:hover": {backgroundColor: theme.colors.gray200},
      }}
      onClick={onClick}
    />
  );
}

const MobileActionsRow = ({
  guestId,
  bpLink,
  saveDisabled,
  onTemplatesOpen,
  attachedImages,
  setAttachedImages,
  insertText,
  sendMessage,
  activeChannel,
  setActiveChannel,
  whatsappData,
  disabled,
  setAvailableChannels,
  availableChannels,
  allChannelsDisabled,
  lastMessage,
  noteText,
  setNoteText,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const imgInputRef = useRef(null);
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {setNotes} = useContext(MessagesContext);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [autoMessageOpen, setAutoMessageOpen] = useState(false);
  const {cannedResponses} = useFeatureFlags();
  const isNote = activeChannel === "note";
  const isWhatsapp = activeChannel === "whatsapp";
  const isEmail = activeChannel === "email";
  const disableAttachments =
    isNote || (activeChannel === "whatsapp" && !whatsappData.freeText);
  const disableImages = isEmail || isWhatsapp;

  const closeMenu = () => setAnchorEl(null);

  const createNote = () => {
    const sent = Date.now();
    const body = {
      enso_user_id: user_profile.enso_user_id,
      guest_id: guestId,
      channel: "note",
      message: noteText,
      enso_key: user_profile.enso_key,
      sender_type: "user",
      sent,
    };

    // Optimistic loading: Add the note to the conversation
    const newLocalMessage = {...body, id: sent};
    dispatch(appendMessage(guestId, newLocalMessage));
    setNotes((prev) => [...prev, newLocalMessage]);

    const action = {
      action_id: "create_message",
      guest_id: guestId,
      body,
    };

    dispatch(
      createAction({
        action,
        guestId,
        disableAlerts: true,
        onError: () => {
          dispatch(
            openGeneralError({
              open: true,
              message: t("alerts.create-note.error.title"),
              disableSeverity: true,
              subtitle: t("alerts.create-note.error.subtitle"),
              onClose: () => dispatch(closeGeneralError()),
            }),
          );
          dispatch(
            editMessages({...newLocalMessage, sending_error: true}, sent),
          );
        },
      }),
    );
    setNoteText("");
  };

  return (
    <>
      <ContextMenu
        menuItems={[
          {
            label: t("inbox.conversations.input.attachments-menu.bp-link"),
            icon: ICONS["boarding-pass-ticket"],
            onClick: () => {
              insertText(bpLink);
              closeMenu();
            },
          },
          disableImages
            ? undefined
            : {
                label: t("inbox.conversations.input.attachments-menu.image"),
                icon: ICONS.image,
                onClick: () => {
                  imgInputRef.current.click();
                  closeMenu();
                },
              },
        ]}
        open={!!anchorEl}
        onClose={closeMenu}
        anchorEl={anchorEl}
        modalProps={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "start",
          },
          modalPosition: {
            vertical: "bottom",
            horizontal: "start",
          },
        }}
      />
      <AttachmentsInput
        guestId={guestId}
        inputRef={imgInputRef}
        attachedImages={attachedImages}
        setAttachedImages={setAttachedImages}
      />
      <FlexContainer
        alignItems="center"
        justifyContent="space-between"
        className={classes.row}
      >
        <FlexContainer
          alignItems="center"
          gap={theme.spacing(2)}
          sx={{flex: 1}}
        >
          <MobileChannelRow
            guestId={guestId}
            disabled={disabled}
            whatsappData={whatsappData}
            activeChannel={activeChannel}
            setActiveChannel={setActiveChannel}
            setAvailableChannels={setAvailableChannels}
            allChannelsDisabled={allChannelsDisabled}
            lastMessage={lastMessage}
          />
          <MobileAutoMessageModal
            isOpen={autoMessageOpen}
            onClose={() => setAutoMessageOpen(false)}
            guestId={guestId}
            whatsappData={whatsappData}
            availableChannels={availableChannels}
          />
          {!isNote && (
            <Divider
              orientation="vertical"
              sx={{height: "22px", margin: theme.spacing(0, 1)}}
            />
          )}
          {!disableAttachments && !isNote && (
            <CircleButton
              icon={"plus"}
              iconProps={{sx: {width: "20px", height: "20px"}}}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            />
          )}
          {/* Bring this back after we fix mobile implementation */}
          {/* {cannedResponses && !isNote && (
            <CircleButton
              icon={"dash-square"}
              iconProps={{sx: {width: "20px", height: "20px"}}}
              onClick={onTemplatesOpen}
            />
          )} */}
          {!isNote && (
            <CircleButton
              icon={"send-scheduled"}
              iconProps={{sx: {width: "20px", height: "20px"}}}
              onClick={() => setAutoMessageOpen(true)}
            />
          )}
        </FlexContainer>
        {isNote ? (
          <ButtonIconText
            label={"Create"}
            weight="semibold"
            typographyVariant="textSM"
            color="white"
            sx={{
              backgroundColor: theme.colors.warning900,
              padding: theme.spacing(1.5, 3.5),
              gap: theme.spacing(2),
              "&:hover": {
                backgroundColor: theme.colors.warning800,
              },
            }}
            onClick={createNote}
            disabled={!noteText?.trim()}
            icon={
              <SvgIcon component={ICONS.note} className={classes.noteButton} />
            }
          />
        ) : (
          <ButtonIcon
            disabled={saveDisabled}
            sx={{padding: theme.spacing(2)}}
            icon={
              <SvgIcon
                component={ICONS.send}
                sx={{width: "20px", height: "20px"}}
              />
            }
            onClick={sendMessage}
          />
        )}
      </FlexContainer>
    </>
  );
};

export default MobileActionsRow;
